(() => {
  const textTypes=new Set(['text-box','text-area','file-upload']);
  const ids=value=>Array.isArray(value)?value:[];
  function visible(control,state,context={}){
    if(!control.active)return false;
    if(!control.condition)return true;
    const results=control.condition.rules.map(rule=>(rule.option==='variant'?[context.variant]:ids(state[rule.option])).includes(rule.value));
    const pass=control.condition.match==='ALL'?results.every(Boolean):results.some(Boolean);
    return control.condition.action==='SHOW'?pass:!pass;
  }
  function settle(controls,state,context={}){
    const byId=new Map(controls.map(c=>[c.id,c]));let next={...state};
    for(let step=0;step<=controls.length;step++){
      const result=Object.fromEntries(Object.entries(next).filter(([id])=>byId.has(id)&&visible(byId.get(id),next,context)));
      if(Object.keys(result).length===Object.keys(next).length)return result;
      next=result;
    }
    throw new Error('Option rules do not settle');
  }
  function validate(control,value){
    const selected=textTypes.has(control.type)?String(value||''):ids(value);
    if(control.required&&!(control.engraving_lines?selected.trim():selected).length)return 'Please complete this option.';
    if(!selected.length)return '';
    if(textTypes.has(control.type)){
      if(control.type==='file-upload')return '';
      const metadata=control.metadata||{},length=Array.from(control.engraving_lines?selected.replace(/\n/g,''):selected).length;
      if(control.engraving_lines&&selected.split('\n').length>control.engraving_lines)return `Please use no more than ${control.engraving_lines} lines.`;
      if(control.min>0&&length<control.min)return `Please enter at least ${control.min} characters.`;
      if(control.max>0&&length>control.max)return `Please use no more than ${control.max} characters.`;
      if(metadata.except_letters&&/\p{L}/u.test(selected))return 'Letters are not available for this option.';
      if(metadata.except_number&&/\p{N}/u.test(selected))return 'Please use letters rather than numbers.';
      if(metadata.except_spaces&&/\s/u.test(selected))return 'Please remove the spaces.';
      if(metadata.except_special_characters&&/[^\p{L}\p{N}\s]/u.test(selected))return 'Please remove special characters.';
      return '';
    }
    if(selected.some(id=>!control.values.some(v=>v.id===id&&!v.hidden)))return 'Please choose an available option.';
    if(!control.multiple&&selected.length>1)return 'Please choose one option.';
    if(control.multiple&&control.min>0&&selected.length<control.min)return `Please choose at least ${control.min} options.`;
    if(control.multiple&&control.max>0&&selected.length>control.max)return `Please choose no more than ${control.max} options.`;
    return '';
  }
  function charge(control,value){
    if(!value||!value.length)return 0;
    if(textTypes.has(control.type)){
      if(control.price_mode!=='per_letter')return control.option_price_pence||0;
      let text=String(value);const m=control.metadata||{};
      if(m.priceNotSpace)text=text.replace(/\s/gu,'');
      if(m.noChargesSpecialChars?.enabled){const free=new Set(Array.from(m.noChargesSpecialChars.characters||''));text=Array.from(text).filter(c=>!free.has(c)).join('');}
      return (control.option_price_pence||0)*Array.from(text).length;
    }
    return ids(value).reduce((sum,id)=>sum+(control.values.find(v=>v.id===id)?.price_pence||0),control.option_price_pence||0);
  }
  function result(controls,state,context={}){
    const settled=settle(controls,state,context),properties={},errors=[],conflicts=[];let additional=0;
    for(const control of controls){
      if(!visible(control,settled,context))continue;
      const value=settled[control.id],error=validate(control,value);
      if(error){errors.push({id:control.id,message:error});continue;}
      if(!value||!value.length)continue;
      const text=textTypes.has(control.type)?String(value):ids(value).map(id=>control.values.find(v=>v.id===id).label).join(', ');
      const property=control.cart_property||control.property;
      if(control.engraving_lines){
        text.split('\n').forEach((line,i)=>{if(!line.trim())return;const key=property.trim()+' — Line '+(i+1);if(Object.hasOwn(properties,key)&&properties[key]!==line)conflicts.push(key);else properties[key]=line;});
      }else if(Object.hasOwn(properties,property)&&properties[property]!==text)conflicts.push(property);
      else properties[property]=text;
      additional+=charge(control,value);
    }
    return {state:settled,properties,additional,errors,conflicts,complete:errors.length===0&&conflicts.length===0};
  }
  function birthstoneGroups(controls){
    return controls.filter(c=>c.active&&c.required&&!c.multiple&&c.property.trim()==='Birthstone Style').map(parent=>{
      const children=controls.filter(c=>c.active&&c.required&&c.type==='product-list'&&!c.multiple&&c.condition?.action==='SHOW'&&c.condition.match==='ALL'&&c.condition.rules.length===1&&c.condition.rules[0].option===parent.id);
      const choices=parent.values.filter(v=>!v.hidden).map(value=>({value,child:children.find(c=>c.condition.rules[0].value===value.id)}));
      return choices.length>1&&choices.every(choice=>choice.child)&&new Set(choices.map(choice=>choice.child.id)).size===choices.length?{parent,choices}:null;
    }).filter(Boolean);
  }
  function paidSelections(controls,state,context={}){
    const selections=[];
    for(const control of controls){
      const value=state[control.id];if(!value?.length||!visible(control,state,context))continue;
      if(textTypes.has(control.type)){
        if(!charge(control,value))continue;
        if(control.price_mode!=='per_letter')throw Error('This option charge is not configured.');
        let letters=Array.from(String(value));const metadata=control.metadata||{};
        if(metadata.priceNotSpace)letters=letters.filter(letter=>!/^\s$/u.test(letter));
        if(metadata.noChargesSpecialChars?.enabled){const free=new Set(Array.from(metadata.noChargesSpecialChars.characters||''));letters=letters.filter(letter=>!free.has(letter));}
        const grouped=new Map();
        for(const letter of letters){
          const upper=letter.toLocaleUpperCase('en-GB'),standard=/^[A-Z]$/.test(upper),label=standard?upper:letter;
          const key=JSON.stringify([control.source_set_id,control.property,control.id,standard?upper:'*',context.material]);
          const identity=key+'|'+label,previous=grouped.get(identity);
          if(previous)previous.units++;else grouped.set(identity,{key,price:control.option_price_pence,units:1,properties:{[control.property]:String(value),Initial:label}});
        }
        selections.push(...grouped.values());continue;
      }
      for(const id of ids(value)){
        const choice=control.values.find(item=>item.id===id);if(!choice?.price_pence)continue;
        const bar=control.source_set_id==='783151'&&control.property==='Engravable Bars';
        const parts=[control.source_set_id,control.property,id,''];if(bar)parts.push(context.material);
        selections.push({key:JSON.stringify(parts),price:bar?(control.component_unit_price_pence||300):choice.price_pence,
          units:bar?(choice.component_units??choice.price_pence/300):1,properties:{[control.cart_property||control.property]:choice.label}});
      }
    }
    return selections;
  }
  window.OrliPersonalisationRules={textTypes,visible,settle,validate,charge,result,birthstoneGroups,paidSelections};
})();
